/**
 * AvatarWithEdit — Wraps LegacyAvatar and adds an "Edit" button overlay
 * when the user has an original photo and editing is permitted.
 *
 * The edit button triggers a callback; the parent component is responsible
 * for opening the CropModal with the appropriate image URL and crop params.
 *
 * Feature: avatar-crop
 * Requirements: 7.1, 7.2, 9.1, 12.1
 */

import { Pencil } from 'lucide-react';
import { LegacyAvatar } from '@/components/ui/LegacyAvatar';
import type { LegacyAvatarProps } from '@/components/ui/LegacyAvatar';
import type { CropParams } from '@/lib/crop-constraints';

export interface AvatarWithEditProps extends LegacyAvatarProps {
  /** Whether the edit button should be shown */
  canEdit: boolean;
  /** Original image S3 key (needed to open crop modal) */
  originalPhotoS3Key?: string | null;
  /** Existing crop params for re-crop initialization */
  cropParams?: CropParams | null;
  /** Callback when edit is clicked */
  onEditClick: () => void;
}

/**
 * Avatar display with an optional "Edit" overlay button for triggering
 * the crop flow. The edit button appears when:
 * - `originalPhotoS3Key` exists (user has an uploaded photo)
 * - `canEdit` is true (user has permission to edit)
 */
export function AvatarWithEdit({
  canEdit,
  originalPhotoS3Key,
  cropParams,
  onEditClick,
  ...avatarProps
}: AvatarWithEditProps): JSX.Element {
  const showEditButton = canEdit && !!originalPhotoS3Key;

  return (
    <div className="relative inline-block">
      <LegacyAvatar {...avatarProps} />
      {showEditButton && (
        <button
          type="button"
          data-testid="avatar-edit-button"
          onClick={onEditClick}
          className="absolute bottom-0 right-0 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors w-6 h-6"
          aria-label="Edit avatar"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
